import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  CircularProgress,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import { EditNoteIcon, ResendIcon, ReactivateIcon } from "../../components/Icons";
import { Layout } from "../../components/layout";
import { Search } from "../../components/Search";
import { Select } from "../../components/Select";
import DataTable, { ColumnConfig } from "../../components/DataTable";
import { useModal, AddUserModalContent, ReassignPatientModalContent } from "../../components/Modal";
import ConfirmActionModalContent from "../../components/Modal/ConfirmAction";
import {
  usersService,
  User,
  UserStatus,
  UserRole,
} from "../../services/usersService";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/selectors/authSelectors";
import lockedFilesImage from "../../assets/images/locked-files.jpg";
import { colors } from "../../theme/colors";
import { UserManagementPageStyled } from "./UserManagementPage.styled";
import StatusBadge from "../../components/StatusBadge";

type SortField = "fullName" | "role" | "email";

interface UserRow extends Record<string, unknown> {
  id: number;
  fullName: string;
  firstName: string;
  lastName: string;
  role: string;
  roleId: number;
  email: string;
  phone: string;
  specialtyIds: number[];
  status: number;
  statusLabel: string;
}

const USERS_PER_PAGE = 25;

const ADD_USER_MODAL_ID = "add-user-modal";

const UserManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const currentUser = useSelector(selectUser);

  const [users, setUsers] = useState<User[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<number | null>(null);
  const [selectedRole, setSelectedRole] = useState<number | null>(null);
  const [sortField, setSortField] = useState<SortField>("fullName");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);

  const isFetchingRef = useRef(false);

  // Debounce search input - 500ms delay to let user finish typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchUsers = useCallback(async () => {
    if (isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoading(true);
    try {
      let orderBy = "FirstName";
      if (sortField === "role") orderBy = "RoleId";
      else if (sortField === "email") orderBy = "Email";

      const statusFilter =
        selectedStatus !== null ? [selectedStatus] : undefined;

      const response = await usersService.getUsers({
        search: debouncedSearch || undefined,
        status: statusFilter,
        roleId: selectedRole !== null ? selectedRole : undefined,
        start: (currentPage - 1) * USERS_PER_PAGE,
        limit: USERS_PER_PAGE,
        total: true,
        orderBy,
        order: sortDirection === "asc" ? 0 : 1,
      });

      setUsers(response.data);
      setTotalCount(response.total);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [
    debouncedSearch,
    selectedStatus,
    selectedRole,
    currentPage,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedStatus, selectedRole]);

  const getStatusLabel = (status: number): string => {
    switch (status) {
      case UserStatus.Active:
        return t("userManagement.status.active");
      case UserStatus.Inactive:
        return t("userManagement.status.inactive");
      case UserStatus.Invited:
        return t("userManagement.status.invited");
      case UserStatus.Suspended:
        return t("userManagement.status.suspended");
      default:
        return "-";
    }
  };

  const getStatusType = (
    status: number,
  ): "active" | "invited" | "suspended" | "inactive" => {
    switch (status) {
      case UserStatus.Active:
        return "active";
      case UserStatus.Inactive:
        return "inactive";
      case UserStatus.Invited:
        return "invited";
      case UserStatus.Suspended:
        return "suspended";
      default:
        return "active";
    }
  };

  const formatPhone = (phone: string): string => {
    if (!phone) return "-";
    const digits = phone.replace(/\D/g, "");
    const localDigits =
      digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    if (localDigits.length === 10) {
      return `+1 (${localDigits.slice(0, 3)}) ${localDigits.slice(
        3,
        6,
      )}-${localDigits.slice(6)}`;
    }
    return phone;
  };

  const userRows: UserRow[] = useMemo(
    () =>
      users.map((u) => ({
        id: u.id,
        fullName: `${u.firstName} ${u.lastName}`.trim(),
        firstName: u.firstName,
        lastName: u.lastName,
        role: u.roleTitle,
        roleId: u.roleId,
        email: u.email,
        phone: u.phoneNumber,
        specialtyIds: u.specialtyIds || [],
        status: u.status,
        statusLabel: getStatusLabel(u.status),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, t],
  );

  const handleUserClick = (userId: number) => {
    navigate(`/user-management/${userId}`);
  };

  const handleEditClick = (userId: number) => {
    navigate(`/user-management/${userId}/edit`);
  };

  const handleAddNewUser = useCallback(() => {
    openModal({
      id: ADD_USER_MODAL_ID,
      content: (
        <AddUserModalContent
          onSuccess={() => {
            closeModal(ADD_USER_MODAL_ID);
            fetchUsers();
          }}
          onClose={() => closeModal(ADD_USER_MODAL_ID)}
        />
      ),
      showCloseButton: true,
      maxWidth: false,
      width: 1056,
      containerPadding: "0",
    });
  }, [openModal, closeModal, fetchUsers]);

  // Check if current user is Manager (roleId === 1)
  const isManager = currentUser?.roleId === UserRole.Manager;

  const handleResendInvite = (user: UserRow) => {
    openModal({
      id: "resend-invite-modal",
      content: (
        <ConfirmActionModalContent
          title={t("userManagement.resendInvite.title")}
          message={t("userManagement.resendInvite.message")}
          confirmText={t("userManagement.resendInvite.confirm")}
          onConfirm={async () => {
            await usersService.resendInvite(user.id);
            closeModal("resend-invite-modal");
            fetchUsers();
          }}
          onCancel={() => closeModal("resend-invite-modal")}
        />
      ),
      maxWidth: 480,
    });
  };

  const openReassignPatientModal = (suspendedProviderId: number) => {
    openModal({
      id: "reassign-patient-modal",
      showCloseButton: true,
      containerPadding: "60px 90px 90px 90px",
      content: (
        <ReassignPatientModalContent
          suspendedProviderId={suspendedProviderId}
          onReassign={() => {
            closeModal("reassign-patient-modal");
            fetchUsers();
          }}
          onCancel={() => {
            closeModal("reassign-patient-modal");
            fetchUsers();
          }}
        />
      ),
      maxWidth: 636,
    });
  };

  const handleSuspendUser = (user: UserRow) => {
    openModal({
      id: "suspend-user-modal",
      showCloseButton: true,
      containerPadding: "60px 90px 90px 90px",
      content: (
        <ConfirmActionModalContent
          icon={lockedFilesImage}
          title={t("userManagement.suspend.title")}
          message={t("userManagement.suspend.message")}
          confirmText={t("userManagement.suspend.confirm")}
          confirmButtonColor="error"
          onConfirm={async () => {
            closeModal("suspend-user-modal");
            if (user.roleId === UserRole.Provider) {
              openReassignPatientModal(user.id);
            } else {
              await usersService.suspendUser(user.id);
              fetchUsers();
            }
          }}
          onCancel={() => closeModal("suspend-user-modal")}
        />
      ),
      maxWidth: 636,
    });
  };

  const handleReactivateUser = (user: UserRow) => {
    openModal({
      id: "reactivate-user-modal",
      content: (
        <ConfirmActionModalContent
          title={t("userManagement.reactivate.title")}
          message={t("userManagement.reactivate.message")}
          confirmText={t("userManagement.reactivate.confirm", "Confirm")}
          onConfirm={async () => {
            await usersService.reactivateUser(user.id);
            closeModal("reactivate-user-modal");
            fetchUsers();
          }}
          onCancel={() => closeModal("reactivate-user-modal")}
        />
      ),
      maxWidth: 480,
    });
  };

  const statusOptions = [
    { value: "", label: t("userManagement.filters.allStatuses") },
    { value: UserStatus.Active, label: t("userManagement.status.active") },
    { value: UserStatus.Invited, label: t("userManagement.status.invited") },
    {
      value: UserStatus.Suspended,
      label: t("userManagement.status.suspended"),
    },
  ];

  const roleOptions = [
    { value: "", label: t("userManagement.filters.allRoles") },
    { value: UserRole.Provider, label: t("userManagement.roles.provider") },
    { value: UserRole.Assistant, label: t("userManagement.roles.assistant") },
    { value: UserRole.Manager, label: t("userManagement.roles.manager") },
  ];

  const columns: ColumnConfig<UserRow>[] = [
    {
      key: "fullName",
      title: t("userManagement.columns.fullName"),
      sortable: true,
      render: (value: unknown, row: UserRow) => (
        <Typography
          className="clickable"
          onClick={() => handleUserClick(row.id)}
        >
          {String(value)}
        </Typography>
      ),
    },
    {
      key: "role",
      title: t("userManagement.columns.role"),
    },
    {
      key: "email",
      title: t("userManagement.columns.email"),
    },
    {
      key: "phone",
      title: t("userManagement.columns.phone"),
      render: (value: unknown) => formatPhone(String(value)),
    },
    {
      key: "specialty",
      title: t("userManagement.columns.specialty"),
      render: (value: unknown, row: UserRow) =>
        row.roleId === UserRole.Provider && value ? (
          <Typography className="specialty-text">{String(value)}</Typography>
        ) : (
          <Typography className="no-specialty-text">-</Typography>
        ),
    },
    {
      key: "statusLabel",
      title: t("userManagement.columns.status"),
      align: "center",
      render: (_value: unknown, row: UserRow) => (
        <StatusBadge
          label={row.statusLabel}
          status={getStatusType(row.status)}
          width={90}
        />
      ),
    },
    {
      key: "actions",
      title: t("userManagement.columns.actions"),
      align: "right",
      render: (_value: unknown, row: UserRow) => (
        <Box className="actions-cell">
          {row.status !== UserStatus.Suspended && (
            <IconButton
              size="small"
              onClick={() => handleEditClick(row.id)}
              sx={{ color: colors.primary.main }}
            >
              <EditNoteIcon width={24} height={24} />
            </IconButton>
          )}
          {row.status === UserStatus.Invited && (
            <IconButton
              size="small"
              onClick={() => handleResendInvite(row)}
              sx={{ color: "#4CAF50" }}
            >
              <ResendIcon width={24} height={24} />
            </IconButton>
          )}
          {row.status === UserStatus.Suspended && (
            <IconButton
              size="small"
              onClick={() => handleReactivateUser(row)}
              sx={{ color: "#4CAF50" }}
            >
              <ReactivateIcon width={24} height={24} />
            </IconButton>
          )}
          <Button
            variant="text"
            size="small"
            className="action-button"
            onClick={() => handleSuspendUser(row)}
            disabled={row.status === UserStatus.Suspended}
            sx={{
              color: colors.status.error,
              "&:disabled": {
                color: colors.text.secondary,
              },
            }}
          >
            {t("userManagement.actions.suspend")}
          </Button>
        </Box>
      ),
    },
  ];

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortField(field as SortField);
    setSortDirection(direction);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleStatusChange = (value: unknown) => {
    setSelectedStatus(value === "" ? null : Number(value));
  };

  const handleRoleChange = (value: unknown) => {
    setSelectedRole(value === "" ? null : Number(value));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <Layout>
      <UserManagementPageStyled>
        <Box className="page-header">
          <Typography variant="h1">{t("userManagement.title")}</Typography>
          {isManager && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNewUser}
              sx={{
                backgroundColor: colors.amber.main,
                "&:hover": {
                  backgroundColor: colors.amber.dark,
                },
              }}
            >
              {t("userManagement.addNewUser")}
            </Button>
          )}
        </Box>

        <Box className="content-header">
          <Box className="filters-container">
            <Select
              label={t("userManagement.filters.showStatuses")}
              value={selectedStatus ?? ""}
              onChange={handleStatusChange}
              options={statusOptions}
              width={200}
            />
            <Select
              label={t("userManagement.filters.userRole")}
              value={selectedRole ?? ""}
              onChange={handleRoleChange}
              options={roleOptions}
              width={200}
            />
            <Search
              width={300}
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t("userManagement.searchPlaceholder")}
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable<UserRow>
            columns={columns}
            data={userRows}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            pageSize={USERS_PER_PAGE}
            currentPage={currentPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            emptyMessage={t("userManagement.noUsersFound")}
          />
        )}
      </UserManagementPageStyled>
    </Layout>
  );
};

export default UserManagementPage;
